import React, { useEffect, useMemo, useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, Save } from "lucide-react";
import GoalGuestStep from "./GoalGuestStep";
import GoalPhotoUpload from "./GoalPhotoUpload";

const todayYmd = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Makassar" });
const parseYmd = (s) => {
  if (!s) return undefined;
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

// Add Record wizard (step 1 guest + treatment, step 2 details).
export const GoalRecordDialog = ({ open, onOpenChange, categories, sources, sourceTypes = [], managers, fixedGuest = null, onSaved }) => {
  const [step, setStep] = useState(1);
  const [guest, setGuest] = useState(null);
  const [treatment, setTreatment] = useState(null);
  const [form, setForm] = useState({
    title: "", category_id: "", date_reported: todayYmd(), source: "", source_type: "", description: "", assigned_to_id: "", photos: [],
  });
  const [calOpen, setCalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1); setGuest(fixedGuest || null); setTreatment(null); setCalOpen(false);
    setForm({ title: "", category_id: "", date_reported: todayYmd(), source: "", source_type: "", description: "", assigned_to_id: "", photos: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fixedGuest?.id]);

  const canSave = useMemo(
    () => !!(
      guest && treatment
      && form.title.trim()
      && form.category_id
      && form.date_reported
      && form.source
      && form.source_type
      && form.assigned_to_id
      && form.description.trim()
    ),
    [guest, treatment, form],
  );

  const save = async () => {
    setSaving(true);
    try {
      await staffApi.post("/goal/records", {
        customer_id: guest.id,
        appointment_id: treatment.appointment_id,
        title: form.title.trim(),
        category_id: form.category_id,
        date_reported: form.date_reported,
        source: form.source,
        source_type: form.source_type,
        description: form.description,
        assigned_to_id: form.assigned_to_id,
        photos: form.photos,
      });
      toast.success("G.O.A.L record created");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="goal-record-dialog">
        <DialogHeader>
          <DialogTitle>Add G.O.A.L record — step {step} of 2</DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Find the guest, then pick the one treatment this opportunity concerns."
              : "Capture the category, when the guest reported it, where it came from and who owns the follow-up."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <GoalGuestStep guest={guest} setGuest={setGuest} treatment={treatment} setTreatment={setTreatment} lockGuest={!!fixedGuest} />
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
              <p className="text-sm font-medium">{guest?.full_name || `${guest?.first_name || ""} ${guest?.last_name || ""}`.trim()}</p>
              <p className="text-xs text-muted-foreground">
                {treatment?.treatment_name} · {treatment?.treatment_date} · {treatment?.branch_name}
              </p>
            </div>

            <div>
              <Label className="text-xs">Title <span className="text-rose-500">*</span></Label>
              <Input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Short title for this complaint or opportunity"
                className="h-10"
                data-testid="goal-field-title"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category <span className="text-rose-500">*</span></Label>
                <Select value={form.category_id} onValueChange={(v) => setForm((f) => ({ ...f, category_id: v }))}>
                  <SelectTrigger data-testid="goal-field-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`goal-category-opt-${c.id}`}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Date reported <span className="text-rose-500">*</span></Label>
                <div className="flex gap-2">
                  <Input value={form.date_reported} readOnly className="h-10" data-testid="goal-field-date" />
                  <Popover open={calOpen} onOpenChange={setCalOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" data-testid="goal-field-date-btn">
                        <CalendarDays className="w-4 h-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end" data-testid="goal-field-date-calendar">
                      <Calendar
                        mode="single"
                        selected={parseYmd(form.date_reported)}
                        onSelect={(d) => {
                          if (!d) return;
                          const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                          setForm((f) => ({ ...f, date_reported: ymd }));
                          setCalOpen(false);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div>
                <Label className="text-xs">Source <span className="text-rose-500">*</span></Label>
                <Select value={form.source} onValueChange={(v) => setForm((f) => ({ ...f, source: v }))}>
                  <SelectTrigger data-testid="goal-field-source"><SelectValue placeholder="Where did it come from?" /></SelectTrigger>
                  <SelectContent>
                    {sources.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`goal-source-opt-${s.replace(/\s+/g, "-").toLowerCase()}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Assigned to <span className="text-rose-500">*</span></Label>
                <Select value={form.assigned_to_id} onValueChange={(v) => setForm((f) => ({ ...f, assigned_to_id: v }))}>
                  <SelectTrigger data-testid="goal-field-assignee"><SelectValue placeholder="Team member" /></SelectTrigger>
                  <SelectContent>
                    {managers.map((m) => (
                      <SelectItem key={m.id} value={m.id} data-testid={`goal-assignee-opt-${m.id}`}>
                        {m.name}{m.role_name ? ` · ${m.role_name}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Source type <span className="text-rose-500">*</span></Label>
              <Select value={form.source_type} onValueChange={(v) => setForm((f) => ({ ...f, source_type: v }))}>
                <SelectTrigger data-testid="goal-field-source-type"><SelectValue placeholder="Which kind of complaint is this?" /></SelectTrigger>
                <SelectContent>
                  {sourceTypes.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`goal-source-type-opt-${s.replace(/\s+/g, "-").toLowerCase()}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Opportunity Description <span className="text-rose-500">*</span></Label>
              <Textarea
                rows={4}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="What did the guest tell us, and what is the opportunity to act, learn and improve?"
                className="resize-none"
                data-testid="goal-field-description"
              />
            </div>

            <div>
              <Label className="text-xs">Photos <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <p className="text-[11px] text-muted-foreground mb-1.5">Attach one or more photos related to this record.</p>
              <GoalPhotoUpload
                photos={form.photos}
                onChange={(next) => setForm((f) => ({ ...f, photos: next }))}
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 2 && (
            <Button variant="outline" onClick={() => setStep(1)} data-testid="goal-record-back">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
          {step === 1 ? (
            <Button disabled={!guest || !treatment} onClick={() => setStep(2)} data-testid="goal-record-next">
              Next
            </Button>
          ) : (
            <Button disabled={!canSave || saving} onClick={save} data-testid="goal-record-save">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save record"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GoalRecordDialog;
